import React from 'react';
import { TopBar } from '../../NavigationBar/TopBar';
import { AgentsList } from './AgentsList';
import { useAgents } from '../../hooks/useAgents';
import type { AuthorizedAgent } from '@/api/useAgentsApi';

/**
 * Premium "Agents" view: the MCP agents (OAuth clients) the user has authorized,
 * with de-authorization and a per-agent toggle for personal scope plus every
 * organization the user belongs to. Access is Glue-owned (see agent.service.ts),
 * so it works for every client regardless of the OAuth scopes it registered with.
 */
export const AgentsLayout: React.FC = () => {
    const { agents, isLoading, orgs, revokeAgent, setOrgAccess, setPersonalAccess } = useAgents();

    const handleRevoke = (agent: AuthorizedAgent) => revokeAgent(agent.id);
    const handleSetOrgAccess = (agent: AuthorizedAgent, orgId: string, allowed: boolean) =>
        setOrgAccess(agent.id, orgId, allowed);
    const handleSetPersonalAccess = (agent: AuthorizedAgent, allowed: boolean) => setPersonalAccess(agent.id, allowed);

    return (
        <div className="bg-background min-h-screen max-h-screen grid grid-cols-1 grid-rows-[54px_calc(100vh-54px)]">
            <TopBar />
            <div className="relative overflow-y-auto">
                <div className="px-6 py-6">
                    <div className="max-w-3xl mx-auto w-full mb-5">
                        <h1 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Authorized Agents</h1>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                            Choose which scopes each agent can reach. Personal access is on by default; toggle it or any
                            organization below.
                        </p>
                    </div>
                    {isLoading ? (
                        <p className="text-sm text-neutral-500 text-center py-20">Loading…</p>
                    ) : (
                        <AgentsList
                            agents={agents}
                            orgs={orgs}
                            onRevoke={handleRevoke}
                            onSetOrgAccess={handleSetOrgAccess}
                            onSetPersonalAccess={handleSetPersonalAccess}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
